import { Tab, tabClasses, Tabs, tabsClasses } from '@mui/material';
import { styled } from '@mui/material/styles';
import { SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from 'shared/hooks';

import { JobStatus } from '../../types';

interface MyJobsPageTabsProps {
    onChange: (status: JobStatus | 'all') => void;
    selectedTab: JobStatus | 'all';
    countersMap: Partial<Record<JobStatus, number>> & { ExternalJob?: number };
    showExternalJobs: boolean;
}

export default function MyJobsPageTabs({ onChange, selectedTab, countersMap, showExternalJobs }: MyJobsPageTabsProps) {
    const { t } = useTranslation('myJobs');
    const isMobile = useIsMobile();

    const handleTabChange = (_: SyntheticEvent<Element, Event>, newTab: JobStatus | 'all') => {
        onChange(newTab);
    };

    const countersLoaded = countersMap.confirmed !== undefined;

    let tabsList: (JobStatus | 'all')[] = [];
    let confirmedSubTabList: (JobStatus | 'all')[] = [];

    if (showExternalJobs && isMobile) {
        tabsList = [ 'confirmed', 'applied', 'rejected' ];
        confirmedSubTabList = [ 'confirmed', 'externalJob' ];
    } else if (showExternalJobs) {
        tabsList = [ 'confirmed', 'externalJob', 'applied', 'rejected' ];
    } else {
        tabsList = [ 'confirmed', 'applied', 'rejected', 'all' ];
    }


    const getTabDisplaySettings = (tab: JobStatus | 'all', withCounters: boolean) => {
        let counter = 0;

        if (tab === 'all') {
            counter = Object.entries(countersMap)
                .reduce((sum, [ , count ]) => sum + (count ?? 0), 0);
        } else if (tab === 'confirmed' && isMobile) {
            counter = (countersMap.confirmed ?? 0) + (countersMap['externalJob'] ?? 0);
        } else {
            counter = countersMap[tab] ?? 0;
        }

        return {
            value: tab,
            label: [
                t(`status.${ tab }.title`),
                countersLoaded && withCounters ? `(${ counter })` : null
            ].filter(x => x !== null).join(' ')
        };
    };

    const tabs = tabsList.map(tab => getTabDisplaySettings(tab, true));
    const confirmedSubTabs = confirmedSubTabList.map(tab => getTabDisplaySettings(tab, false));

    return (
        <>
            <StyledTabs
                value={ selectedTab === 'externalJob' ? 'confirmed' : selectedTab }
                onChange={ handleTabChange }
            >
                { tabs.map(({ value, label }) => (
                    <StyledTab
                        key={ value }
                        label={ label }
                        value={ value }
                        wrapped
                    />
                )) }
            </StyledTabs>
            { confirmedSubTabs?.length > 0 && (selectedTab === 'confirmed' || selectedTab === 'externalJob') && (
                <StyledTabs
                    value={ selectedTab }
                    onChange={ handleTabChange }
                >
                    { confirmedSubTabs.map(({ value, label }) => (
                        <StyledTab
                            key={ value }
                            label={ label }
                            value={ value }
                            wrapped
                        />
                    )) }
                </StyledTabs>
            ) }
        </>
    );
}

const StyledTabs = styled(Tabs)(({ theme }) => ({
    [`.${ tabsClasses.list }`]: {
        padding: theme.spacing(1),
        background: '#ECEFF1',
        borderRadius: theme.shape.borderRadius
    },

    [`.${ tabsClasses.indicator }`]: {
        display: 'none'
    },

    minHeight: theme.spacing(4)
}));

const StyledTab = styled(Tab)(({ theme }) => ({
    borderWidth: 0,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.text.secondary,

    [`&.${ tabClasses.selected }`]: {
        borderWidth: 1,
        borderColor: theme.palette.divider,
        backgroundColor: theme.palette.primary.main,
        boxShadow: 'rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.1) 0px 1px 2px -1px',
        color: theme.palette.primary.contrastText
    },

    [`&.${ tabClasses.root }`]: {
        minHeight: 'auto',
        padding: theme.spacing(1, 2)
    }
}));
